'use client';

import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMarketplaceStore } from '@/lib/marketplace/store';
import type { ListingType, PriceType } from '@/lib/marketplace/types';
import { ListingCard } from './listing-card';
import {
  Search,
  Store,
  SlidersHorizontal,
  Star,
  Download,
  Clock,
} from 'lucide-react';

const LISTING_TYPES: { value: ListingType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'agent_template', label: 'Agent Templates' },
  { value: 'connector', label: 'Connectors' },
  { value: 'skill', label: 'Skills' },
  { value: 'workflow', label: 'Workflows' },
];

const PRICE_TYPES: { value: PriceType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Prices' },
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
  { value: 'freemium', label: 'Freemium' },
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular', icon: Download },
  { value: 'newest', label: 'Newest', icon: Clock },
  { value: 'rating', label: 'Top Rated', icon: Star },
];

export function MarketplaceBrowse() {
  const {
    listings,
    listingsLoading,
    pagination,
    categories,
    searchParams,
    search,
    loadMore,
    loadCategories,
    setSearchParams,
    clearSearch,
  } = useMarketplaceStore();

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    loadCategories();
    search();
  }, [loadCategories, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ query: searchInput });
    search();
  };

  const handleFilterChange = (key: string, value: string) => {
    const newValue = value === 'all' ? undefined : value;
    setSearchParams({ [key]: newValue });
    search();
  };

  const handleSortChange = (value: string) => {
    setSearchParams({ sort_by: value as 'popular' | 'newest' | 'rating' });
    search();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="size-6" />
            Marketplace
          </h1>
          <p className="text-muted-foreground mt-1">
            Discover agent templates, connectors, and skills
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search marketplace..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit">Search</Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="size-4 mr-2" />
            Filters
          </Button>
        </form>

        {/* Filter Bar */}
        {showFilters && (
          <div className="flex flex-wrap gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select
                value={searchParams.listing_type || 'all'}
                onValueChange={(v) => handleFilterChange('listing_type', v)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LISTING_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select
                value={searchParams.category || 'all'}
                onValueChange={(v) => handleFilterChange('category', v)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Price</label>
              <Select
                value={searchParams.price_type || 'all'}
                onValueChange={(v) => handleFilterChange('price_type', v)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_TYPES.map((price) => (
                    <SelectItem key={price.value} value={price.value}>
                      {price.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Sort By</label>
              <Select
                value={searchParams.sort_by || 'popular'}
                onValueChange={handleSortChange}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((sort) => (
                    <SelectItem key={sort.value} value={sort.value}>
                      <div className="flex items-center gap-2">
                        <sort.icon className="size-4" />
                        {sort.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto self-end">
              <Button variant="ghost" size="sm" onClick={() => {
                clearSearch();
                setSearchInput('');
                search();
              }}>
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {(searchParams.query || searchParams.listing_type || searchParams.category || searchParams.price_type) && (
          <div className="flex flex-wrap gap-2">
            {searchParams.query && (
              <Badge variant="secondary" className="gap-1">
                Search: {searchParams.query}
              </Badge>
            )}
            {searchParams.listing_type && (
              <Badge variant="secondary" className="gap-1">
                Type: {LISTING_TYPES.find(t => t.value === searchParams.listing_type)?.label}
              </Badge>
            )}
            {searchParams.category && (
              <Badge variant="secondary" className="gap-1">
                Category: {categories.find(c => c.slug === searchParams.category)?.name}
              </Badge>
            )}
            {searchParams.price_type && (
              <Badge variant="secondary" className="gap-1">
                Price: {PRICE_TYPES.find(p => p.value === searchParams.price_type)?.label}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {listingsLoading && listings.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} className="h-[250px]" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Store className="size-12 mx-auto mb-4 opacity-50" />
          <p>No listings found</p>
          <p className="text-sm">Try adjusting your filters or search terms</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{pagination.total} results</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>

          {pagination.hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={listingsLoading}
              >
                {listingsLoading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
